/**
 * Groups API - Common Types
 *
 * Shared schemas used across group endpoints.
 */

import { z } from 'zod'
import { AppVisibilitySchema } from '../spaces/common'

export { AppVisibilitySchema }

// ============================================================================
// Group Theme
// ============================================================================

// Banner / Card / ThemeVariant mirror the GroupDesignTheme TS interface in
// apps/backend/src/services/user-groups/shared/types.ts. Keep these in sync
// when the type evolves.
const BannerConfigSchema = z.object({
  backgroundColor: z.string(),
  titleColor: z.string(),
  iconColor: z.string(),
  icon: z.string().optional()
})

const CardConfigSchema = z.object({
  backgroundColor: z.string(),
  titleColor: z.string(),
  iconColor: z.string(),
  icon: z.string().optional()
})

const ThemeVariantSchema = z.object({
  banner: BannerConfigSchema,
  card: CardConfigSchema
})

export const GroupDesignThemeSchema = z.object({
  light: ThemeVariantSchema,
  dark: ThemeVariantSchema
})

export type GroupDesignTheme = z.infer<typeof GroupDesignThemeSchema>

export const GroupThemeSchema = z
  .object({
    bannerBackgroundColor: z.string().nullable(),
    bannerLogoUrl: z.string().nullable(),
    bannerImageUrl: z.string().nullable(),
    avatarImageUrl: z.string().nullable(),
    designTheme: GroupDesignThemeSchema.nullable().optional()
  })
  .nullable()

export type GroupTheme = z.infer<typeof GroupThemeSchema>

// ============================================================================
// Group Settings
// ============================================================================

export const GroupSettingsAPISchema = z.object({
  memberListEnabled: z.boolean(),
  commentsEnabled: z.boolean(),
  reactionsEnabled: z.boolean(),
  sharingEnabled: z.boolean(),
  memberPostingEnabled: z.boolean(),
  chatEnabled: z.boolean().optional(),
  mediaLibraryEnabled: z.boolean().optional()
})

export type GroupSettingsAPI = z.infer<typeof GroupSettingsAPISchema>

/**
 * Partial-update body for PATCH /v1/groups/:group_id/settings.
 *
 * Every field is optional — only the fields present in the request are
 * written to the `UserGroup` row (partial update). `.strict()` rejects
 * unknown keys so a client-side typo never silently no-ops.
 *
 * `showInApp` is the canonical visibility flag for CUSTOM/community spaces
 * (a leader turning the space on/off). Section-backed spaces persist
 * visibility on `Section.showInApp` via the schools-level section endpoint
 * instead — see plan 004 KTD3.
 */
export const GroupSettingsUpdateSchema = z
  .object({
    chatEnabled: z.boolean(),
    mediaLibraryEnabled: z.boolean(),
    memberListEnabled: z.boolean(),
    memberPostingEnabled: z.boolean(),
    commentsEnabled: z.boolean(),
    reactionsEnabled: z.boolean(),
    sharingEnabled: z.boolean(),
    showInApp: z.boolean(),
    // 3-state visibility override. Takes precedence over showInApp when both are
    // present; the backend derives app_visibility from showInApp when absent.
    appVisibility: AppVisibilitySchema
  })
  .partial()
  .strict()

export type GroupSettingsUpdate = z.infer<typeof GroupSettingsUpdateSchema>

// ============================================================================
// Group Permissions
// ============================================================================

export const GroupPermissionsSchema = z
  .object({
    isMember: z.boolean(),
    canView: z.boolean(),
    canReact: z.boolean(),
    canComment: z.boolean(),
    canPost: z.boolean(),
    canModerate: z.boolean()
  })
  .nullable()
  .optional()

export type GroupPermissions = z.infer<typeof GroupPermissionsSchema>

// ============================================================================
// Group School Reference
// ============================================================================

export const GroupSchoolRefSchema = z
  .object({
    id: z.string().nullable(),
    name: z.string().nullable()
  })
  .nullable()

// ============================================================================
// Group Section
// ============================================================================

export const GroupSectionSchema = z
  .object({
    id: z.string(),
    // Section public ID — additive (PR-1 U5). Used by the web group-settings
    // dialog to route Bucket A writes to the schools-level section endpoint.
    // Optional on the shared-types side to keep mobile clients compatible while
    // backend rolls out; backend always projects it for section-backed rows.
    publicId: z.string().optional(),
    name: z.string(),
    offeringType: z.string().nullable(),
    schoolLevel: z.string().nullable(),
    schoolYear: z.string().nullable(),
    durationName: z.string().nullable(),
    courseCode: z.string().nullable().optional(),
    roomName: z.string().nullable().optional(),
    venueName: z.string().nullable().optional(),
    seasonName: z.string().nullable().optional(),
    personCount: z.number().nullable().optional(),
    lastSyncedAt: z.string().nullable().optional()
  })
  .nullable()
  .optional()

// ============================================================================
// Stream Chat Integration
// ============================================================================

export const GroupStreamChatSchema = z
  .object({
    channelId: z.string().nullable(),
    status: z.string().nullable(),
    syncedAt: z.string().nullable(),
    lastError: z.string().nullable()
  })
  .nullable()
  .optional()

// ============================================================================
// Member Permissions
// ============================================================================

export const MemberPermissionsSchema = z.object({
  canView: z.boolean(),
  canReact: z.boolean(),
  canComment: z.boolean(),
  canPost: z.boolean(),
  canModerate: z.boolean(),
  canInvite: z.boolean()
})

// ============================================================================
// Notification Level
// ============================================================================

export const NotificationLevelSchema = z.enum(['NONE', 'MENTIONS', 'IMPORTANT', 'ALL'])
export type NotificationLevel = z.infer<typeof NotificationLevelSchema>

// ============================================================================
// Member Notifications
// ============================================================================

export const MemberNotificationsSchema = z.object({
  showInTimeline: z.boolean(),
  notifyLevel: NotificationLevelSchema,
  notifyFeed: z.boolean(),
  notifyPush: z.boolean(),
  notifyEmail: z.boolean(),
  notifySms: z.boolean()
})

// ============================================================================
// Group Member Role
// ============================================================================

export const GroupMemberRoleAPISchema = z.enum(['OWNER', 'ADMIN', 'MEMBER', 'MODERATOR', 'GUEST', 'VIEWER'])

// ============================================================================
// Group Membership Details
// ============================================================================

/**
 * How a group-membership row was created. Surfaced read-only so UI can label
 * relationship-inferred members distinctly from explicitly-rostered ones.
 * NOT a permission marker — INFERRED rows already have `canPost / canComment`
 * forced to false at the resolver/backfill layer.
 */
export const MemberOriginSchema = z.enum(['DIRECT', 'INFERRED', 'MANUAL', 'UNKNOWN'])
export type MemberOrigin = z.infer<typeof MemberOriginSchema>

export const GroupMembershipDetailsSchema = z.object({
  id: z.string(),
  permissionLevel: z.string(),
  identityRole: z.string(),
  sectionRole: z.string(),
  memberOrigin: MemberOriginSchema.optional(),
  isLeader: z.boolean(),
  isHeadRole: z.boolean(),
  memberPermissions: MemberPermissionsSchema,
  notifications: MemberNotificationsSchema,
  joinedAt: z.union([z.string(), z.date()]),
  removedAt: z.union([z.string(), z.date()]).nullable(),
  lastInteractionAt: z.union([z.string(), z.date()]).nullable(),
  status: z.string()
})

export type GroupMembershipDetails = z.infer<typeof GroupMembershipDetailsSchema>

// ============================================================================
// School User Base (for group members)
// ============================================================================

export const SchoolUserBaseSchema = z.object({
  id: z.string(),
  platformId: z.string().nullable(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  preferredFirstName: z.string().nullable(),
  preferredLastName: z.string().nullable(),
  maidenName: z.string().nullable().optional(),
  displayName: z.string().nullable().optional(),
  photoUrlThumbnail: z.string().nullable().optional(),
  photoUrlLarge: z.string().nullable().optional(),
  status: z.string().nullable(),
  email: z.string().nullable().optional(),
  isAppUser: z.boolean().optional(),
  roles: z.object({
    isStudent: z.boolean(),
    isParent: z.boolean(),
    isFaculty: z.boolean(),
    isTeacher: z.boolean().optional(),
    isAdmin: z.boolean().optional(),
    isAdvisor: z.boolean().optional(),
    isCoach: z.boolean().optional(),
    isAlumni: z.boolean().optional(),
    isBetaTester: z.boolean().optional(),
    isSkyApiSyncEnabled: z.boolean().optional()
  }),
  school: z
    .object({
      id: z.string(),
      name: z.string()
    })
    .nullable()
    .optional(),
  lastSyncedAt: z.string().nullable().optional()
})

export type SchoolUserBase = z.infer<typeof SchoolUserBaseSchema>

// ============================================================================
// Group Member Type (with membership details)
// ============================================================================

export const GroupMemberTypeSchema = SchoolUserBaseSchema.extend({
  groupMembership: GroupMembershipDetailsSchema
})

export type GroupMemberType = z.infer<typeof GroupMemberTypeSchema>
